import React from 'react';
import { Palette, Type, ShieldAlert, Sparkles, Check } from 'lucide-react';
import { ColorSwatch, CustomProject } from '../../types/productBuilder';

interface ProjectDNAProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  presetSwatches: ColorSwatch[];
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const ProjectDNA: React.FC<ProjectDNAProps> = ({ project, onUpdateProject, presetSwatches, onAddLog }) => {
  
  const handleSwatchSelect = (swatch: ColorSwatch) => {
    onUpdateProject({ ...project, colors: swatch });
    onAddLog('Product Builder', `Changed project brand colors: "${swatch.swatchName}"`, 'SUCCESS', 'Color swatch parameters applied.');
  };

  const handleFontSelect = (font: string) => {
    onUpdateProject({ ...project, typography: font });
    onAddLog('Product Builder', `Styled typography font matrix: "${font}"`, 'SUCCESS', 'Font rules updated.');
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdateProject({ ...project, logoText: e.target.value });
  };

  return (
    <div className="npx-builder-dna-container space-y-6 text-left animate-fade-in">
      <div className="border-b border-white/5 pb-4">
        <div className="flex items-center gap-1.5">
          <Palette className="w-4 h-4 text-[#00ffff]" />
          <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">4. Project DNA</h2>
        </div>
        <p className="text-xs text-zinc-400 mt-1">Configure physical branding parameters (color swatches, display typography, and corporate identity logos) for your preview layout.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        
        {/* BRAND SCHEME */}
        <div className="space-y-4 bg-black/20 p-5 rounded-2xl border border-white/5">
          <h3 className="text-xs font-bold font-mono text-white uppercase tracking-wider flex items-center gap-2">
            <Palette className="w-4 h-4 text-[#00ffff]" />
            <span>Colur Swatch Matrices</span>
          </h3>

          <div className="space-y-2 max-h-[220px] overflow-y-auto custom-scrollbar">
            {presetSwatches.map((sw, idx) => {
              const active = project.colors?.swatchName === sw.swatchName;
              return (
                <div
                  key={idx}
                  onClick={() => handleSwatchSelect(sw)}
                  className={`p-3 rounded-xl border flex items-center justify-between text-[9px] font-mono transition-all cursor-pointer ${active ? 'bg-zinc-900 border-[#00ffff]/20 text-white' : 'bg-black/40 border-zinc-900 text-zinc-400 hover:border-zinc-805'}`}
                >
                  <div className="flex gap-2 items-center">
                    <span className="font-extrabold uppercase">{sw.swatchName}</span>
                    <div className="flex gap-1">
                      <span className="w-2.5 h-2.5 rounded-full border border-white/10" style={{ backgroundColor: sw.primary }}></span>
                      <span className="w-2.5 h-2.5 rounded-full border border-white/10" style={{ backgroundColor: sw.secondary }}></span>
                      <span className="w-2.5 h-2.5 rounded-full border border-white/10" style={{ backgroundColor: sw.darkAccent }}></span>
                    </div>
                  </div>
                  {active && <Check size={11} className="text-[#00ffff]" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* TYPOGRAPHY & IDENTITY */}
        <div className="space-y-4 bg-black/20 p-5 rounded-2xl border border-white/5 flex flex-col justify-between self-stretch">
          <div className="space-y-4">
            <h3 className="text-xs font-bold font-mono text-white uppercase tracking-wider flex items-center gap-2">
              <Type className="w-4 h-4 text-purple-400" />
              <span>Typography Pairing</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { name: 'Space Grotesk', desc: 'Tech-forward geometry headers' },
                { name: 'Inter', desc: 'Standard Swiss modernist curves' },
                { name: 'Outfit', desc: 'Clean, futuristic, lightweight tracker' },
                { name: 'Playfair Display', desc: 'Editorial serif italics' },
                { name: 'JetBrains Mono', desc: 'Monospacing high-density logs' }
              ].map((fn) => {
                const active = project.typography === fn.name;
                return (
                  <button
                    key={fn.name}
                    type="button"
                    onClick={() => handleFontSelect(fn.name)}
                    className={`p-2.5 rounded-xl border text-left flex flex-col transition-all cursor-pointer ${active ? 'bg-zinc-900 border-[#00ffff]/20 text-white' : 'bg-black/40 border-zinc-900 text-zinc-450 hover:border-zinc-800'}`}
                  >
                    <span className="text-[10px] uppercase font-bold font-mono">{fn.name}</span>
                    <span className="text-[8px] text-zinc-500 normal-case leading-normal mt-0.5">{fn.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2 pt-3 border-t border-white/5">
            <label className="text-[9.5px] font-mono text-zinc-400 uppercase font-bold block">Active Corporate Identity text</label>
            <input
              type="text"
              value={project.logoText || ''}
              onChange={handleLogoChange}
              placeholder="e.g. NexoraX Care"
              className="w-full bg-black/40 border border-zinc-900 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#00ffff] font-mono uppercase"
            />
          </div>
        </div>

      </div>

      <div className="p-4 bg-zinc-950/40 border border-white/5 rounded-2xl flex items-start gap-3">
        <ShieldAlert size={15} className="text-yellow-500 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-[11px] font-mono text-white uppercase font-bold">Secure brand styling guidelines</h4>
          <p className="text-[9.5px] text-zinc-500 leading-relaxed font-sans normal-case">
            All brand configs in Project DNA apply specifically to the customer layout canvas during this active session and are stored locally in the preview sandbox. They cannot overwrite global website CSS variables, main navigation structures, footers, or headers.
          </p>
        </div>
      </div>
    </div>
  );
};
